import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { GenericMatAutocompleteComponent } from '@/common/generic-mat-autocomplete/generic-mat-autocomplete.component';
import { environment } from 'src/environments/environment';
import {
	TermTourismEventSetKinds,
	TourismEventsWithDelay,
	VehicleModel,
} from '@/bag/bag-transport-term/bag-transport-term.model';
import { TransportSegmentsApiService } from '@/bag/transport-segments/transport-segments.api.service';
import { Segment } from '@/bag/transport-segments/transport-segments.model';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { LoaderService } from '@/common/loader/loader.service';
import { firstValueFrom } from 'rxjs';
import { BagTransportTermApiService } from '@/bag/bag-transport-term/bag-transport-term-api.service';
import {
	ChosenTourismEvent,
	TourismEventConnectService,
} from '@/common/tourism-event-connect/tourism-event-connect.service';
import { DateService } from '@/common/date.service';

@Component({
	selector: 'app-segment-assign-tourism-event-dialog',
	standalone: true,
	imports: [MatDialogModule, MatButtonModule, GenericMatAutocompleteComponent],
	templateUrl: './segment-assign-tourism-event-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SegmentAssignTourismEventDialogComponent {
	readonly segment = inject<Segment>(MAT_DIALOG_DATA);
	readonly chosenEvent = signal<ChosenTourismEvent | null>(null);
	readonly apiSearchParam = computed(() =>
		this.tourismEventConnectService.buildTourismEventSearchParam({
			dateFrom: this.dateService.addDays(-3, this.segment.dateFrom),
			dateTo: this.dateService.addDays(3, this.segment.dateTo),
		}),
	);
	readonly environment = environment;
	readonly vehicles = signal<VehicleModel[]>([]);

	private readonly dateService = inject(DateService);
	private readonly tourismEventConnectService = inject(TourismEventConnectService);
	private readonly transportSegmentsApiService = inject(TransportSegmentsApiService);
	private readonly bagTransportTermApiService = inject(BagTransportTermApiService);
	private readonly infobox = inject(InfoboxService);
	private readonly loader = inject(LoaderService);
	private readonly dialogRef = inject(MatDialogRef<SegmentAssignTourismEventDialogComponent>);

	erpEventsMapper = (response: { data: TourismEventsWithDelay[] }): TourismEventsWithDelay[] => {
		return this.tourismEventConnectService.mapTourismEventsForAutocomplete(response);
	};

	async onTourismEventSelected(event: TourismEventsWithDelay): Promise<void> {
		let vehicles = this.vehicles();
		if (!vehicles.length) {
			vehicles = await this.bagTransportTermApiService.getVehiclesData();
			this.vehicles.set(vehicles);
		}

		this.chosenEvent.set(this.tourismEventConnectService.buildChosenEvent(event, vehicles));
	}

	async assignTourismEvent(): Promise<void> {
		const chosenEvent = this.chosenEvent();
		if (!chosenEvent) {
			return;
		}

		this.loader.show();
		try {
			const termTourismEventSetObject = await this.tourismEventConnectService.prepareTermTourismEventSet(
				chosenEvent,
				{ kind: TermTourismEventSetKinds.BOTH },
			);
			if (!termTourismEventSetObject) {
				return;
			}

			const payload = {
				termTourismEvent: termTourismEventSetObject,
				replaceExisting: false,
			};

			await firstValueFrom(this.transportSegmentsApiService.assignTourismEvent(this.segment.id, payload));
			this.infobox.success('Pomyślnie połączono wszystkie pozycje sezonów z segmentu');
			this.dialogRef.close(true);
		} finally {
			this.loader.hide();
		}
	}
}
